import { Link } from "react-router-dom";
import { ArrowRight, Check, Clock3, Eye, LineChart, Lightbulb, ListFilter, MessageSquare, PlaySquare, Share2, Users } from "lucide-react";
import { FaInstagram, FaTiktok, FaTwitch } from "react-icons/fa";
import SEO, { breadcrumbSchema } from "../Components/SEO";
import TheNav from "../Components/navbar";

const PLATFORM_CONTENT = {
  tiktok: {
    icon: FaTiktok,
    color: "#ff2d6f",
    path: "/tiktok-analytics",
    tab: "tiktok",
    label: "TikTok",
    seoTitle: "TikTok Analytics Tools for Creators | Kre8 Content",
    eyebrow: "TikTok analytics tool",
    title: "A TikTok analytics tool that helps you make videos people watch.",
    description: "Use a TikTok analytics tool to compare video views, likes, comments, shares, video length, and available retention signals. Find content patterns worth repeating.",
    intro: "Kre8 is a TikTok analytics tool for creators, streamers, social media managers, and marketers who want a clearer content plan. Compare videos side by side and use their performance signals to test stronger ideas, formats, and hooks.",
    metrics: [
      [Eye, "Video performance", "Compare views, likes, comments, and shares across your TikTok videos."],
      [Clock3, "Attention signals", "Use watch time and retention signals when TikTok makes them available for your account."],
      [Users, "Audience growth", "See followers gained per video when that metric is available."],
      [ListFilter, "Useful sorting", "Sort your posts to quickly find the formats and topics that stand out."],
    ],
    steps: [
      ["Connect your TikTok account", "Authorize your account from the secure connection flow."],
      ["Compare the videos", "Review your posts in one consistent table instead of jumping between individual videos."],
      ["Choose the next experiment", "Use the strongest patterns as a starting point for your next post, ad, or content series."],
    ],
    faqs: [
      ["What TikTok analytics can I track?", "Kre8 can show video views, likes, comments, shares, and video length. Watch time, retention, and followers gained appear when TikTok makes those metrics available to your connected account."],
      ["Who are TikTok analytics tools for?", "Kre8 is built for creators, Twitch streamers repurposing clips, social media managers, marketers, and campaign managers who need a faster way to compare post performance."],
      ["Can TikTok analytics tell me what to post next?", "Analytics cannot guarantee a result, but comparing your strongest and weakest posts can help you form better hypotheses about formats, hooks, topics, and length for the next test."],
    ],
  },
  instagram: {
    icon: FaInstagram,
    color: "#e1306c",
    path: "/instagram-reels-analytics",
    tab: "instagram",
    label: "Instagram Reels",
    seoTitle: "Instagram Reels Analytics Tool for Creators | Kre8 Content",
    eyebrow: "Instagram Reels analytics tool",
    title: "An Instagram Reels analytics tool for a sharper content strategy.",
    description: "Use an Instagram Reels analytics tool to compare Reel likes, comments, estimated views, and comment rates. Spot posts worth building on next.",
    intro: "Kre8 keeps your Instagram Reels analytics alongside TikTok and Twitch activity. It gives creators, social media managers, marketers, and campaign managers one place to compare engagement and decide what to improve next.",
    metrics: [
      [MessageSquare, "Reel engagement", "Review likes and comments for your connected Instagram Reels."],
      [LineChart, "Estimated reach", "See clearly labeled estimated views where available, based on likes rather than a measured view count."],
      [Share2, "Comment rate", "Compare comments against estimated views to identify posts that sparked conversation."],
      [ListFilter, "Post comparison", "Sort your Reels to find the topics and creative directions that earn stronger engagement."],
    ],
    steps: [
      ["Connect your Instagram account", "Authorize an eligible professional account from Kre8."],
      ["Review engagement patterns", "Compare Reel likes and comments without opening each post one by one."],
      ["Plan the next version", "Use the patterns you find to refine the next concept, creative, or campaign."],
    ],
    faqs: [
      ["What Instagram Reels analytics are available?", "Kre8 currently shows Reel likes and comments, with estimated views and comment rates where shown. Estimated views are not measured views and are labeled accordingly."],
      ["Why are Instagram views estimated?", "Some deeper Instagram insight data remains dependent on Meta access. Kre8 labels estimates clearly so you can distinguish them from data returned directly by Instagram."],
      ["Who can use an Instagram Reels analytics tool?", "It is designed for creators, social media managers, marketers, campaign managers, and streamers who want to compare Reel performance in one place."],
    ],
  },
  twitch: {
    icon: FaTwitch,
    color: "#9146ff",
    path: "/twitch-stream-analytics",
    tab: "twitch",
    label: "Twitch",
    seoTitle: "Twitch Stream Analytics Tool for Content Planning | Kre8 Content",
    eyebrow: "Twitch stream analytics tool",
    title: "A Twitch stream analytics tool for finding streams worth repurposing.",
    description: "Use a Twitch stream analytics tool to review recent broadcast dates, durations, and titles. Find streams worth turning into clips, posts, and campaigns.",
    intro: "For Twitch streamers and the teams around them, the best next social post often starts with a broadcast worth revisiting. Kre8 keeps Twitch stream analytics beside short-form performance so you can make smarter decisions about what to turn into content.",
    metrics: [
      [PlaySquare, "Recent broadcasts", "See your connected channel's broadcast dates, durations, and titles in one list."],
      [Clock3, "Flexible time periods", "Filter broadcasts from the last 7, 30, 60, or 90 days."],
      [ListFilter, "Simple sorting", "Sort the broadcast list by date or duration to narrow the next place to look."],
      [Lightbulb, "Repurposing context", "Pair stream history with TikTok and Instagram performance before choosing the next clip or campaign angle."],
    ],
    steps: [
      ["Connect your Twitch channel", "Authorize your Twitch account from the secure connection flow."],
      ["Review recent broadcasts", "Filter and sort your stream history by the period and order that matter for your planning."],
      ["Turn context into a plan", "Use your broadcast history alongside short-form performance to decide what to repurpose next."],
    ],
    faqs: [
      ["What Twitch stream stats does Kre8 show?", "Kre8 currently shows recent broadcast dates, durations, and titles for your connected Twitch channel."],
      ["Can I filter my Twitch broadcasts?", "Yes. You can review broadcasts from the last 7, 30, 60, or 90 days and sort the list by date or duration."],
      ["Is this only for Twitch streamers?", "No. It is also useful for editors, clippers, social media managers, and campaign teams planning content around a Twitch creator's broadcasts."],
    ],
  },
};

function FaqSchema({ content }) {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: content.faqs.map(([question, answer]) => ({
      "@type": "Question",
      name: question,
      acceptedAnswer: { "@type": "Answer", text: answer },
    })),
  };
}

export default function PlatformAnalyticsLanding({ platform }) {
  const content = PLATFORM_CONTENT[platform];
  const Icon = content.icon;
  const signupHref = `/signup?next=${encodeURIComponent(`/stats?tab=${content.tab}`)}`;

  return (
    <div className="min-h-screen overflow-x-hidden bg-[#07060d] text-white">
      <SEO
        title={content.seoTitle}
        description={content.description}
        canonicalPath={content.path}
        structuredData={[
          breadcrumbSchema([
            { name: "Social Media Stats Tracker", path: "/stats" },
            { name: content.label, path: content.path },
          ]),
          FaqSchema({ content }),
        ]}
      />
      <TheNav />
      <main>
        <section className="relative overflow-hidden px-5 pb-20 pt-16 sm:px-8 sm:pb-28 sm:pt-24">
          <div aria-hidden="true" className="pointer-events-none absolute inset-x-0 top-0 h-[580px]" style={{ background: `radial-gradient(50% 62% at 50% 0%, ${content.color}2e, transparent 72%)` }} />
          <div className="relative mx-auto max-w-5xl text-center">
            <p className="inline-flex items-center gap-2 rounded-full border px-3 py-1.5 text-xs font-semibold uppercase tracking-[0.14em]" style={{ borderColor: `${content.color}66`, color: "#f5efff", background: `${content.color}18` }}>
              <Icon aria-hidden="true" className="h-4 w-4" style={{ color: content.color }} /> {content.eyebrow}
            </p>
            <h1 className="mx-auto mt-6 max-w-4xl text-4xl font-semibold leading-[1.03] tracking-[-0.04em] sm:text-6xl">{content.title}</h1>
            <p className="mx-auto mt-6 max-w-2xl text-base leading-relaxed text-white/65 sm:text-lg">{content.intro}</p>
            <div className="mt-9 flex flex-col justify-center gap-3 sm:flex-row">
              <Link to={signupHref} className="inline-flex min-h-12 items-center justify-center gap-2 rounded-full px-6 text-sm font-semibold text-white transition hover:brightness-110" style={{ background: content.color, boxShadow: `0 12px 38px -14px ${content.color}` }}>
                Start your 7-day free trial <ArrowRight aria-hidden="true" className="h-4 w-4" />
              </Link>
              <Link to={`/stats?tab=${content.tab}`} className="inline-flex min-h-12 items-center justify-center rounded-full border border-white/15 px-6 text-sm font-semibold text-white/85 transition hover:bg-white/5">Open the stats tracker</Link>
            </div>
            <p className="mt-4 text-xs text-white/40">Card required. Free for 7 days, then $10 USD/month unless canceled.</p>
          </div>
        </section>

        <section className="mx-auto w-full max-w-6xl px-5 py-16 sm:px-8 sm:py-24">
          <div className="max-w-2xl">
            <p className="text-xs font-semibold uppercase tracking-[0.18em] text-[#84ff00]">What you can use</p>
            <h2 className="mt-3 text-3xl font-semibold tracking-[-0.03em] sm:text-4xl">Use performance data to make the next content decision with more context.</h2>
          </div>
          <div className="mt-10 grid gap-4 md:grid-cols-2">
            {content.metrics.map(([MetricIcon, heading, copy]) => <article key={heading} className="rounded-2xl border border-white/[0.09] bg-white/[0.035] p-6">
              <MetricIcon aria-hidden="true" className="h-5 w-5" style={{ color: content.color }} />
              <h3 className="mt-5 text-lg font-semibold">{heading}</h3>
              <p className="mt-2 text-sm leading-relaxed text-white/60">{copy}</p>
            </article>)}
          </div>
        </section>

        <section className="border-y border-white/[0.08] bg-white/[0.025] px-5 py-16 sm:px-8 sm:py-24">
          <div className="mx-auto max-w-6xl">
            <p className="text-xs font-semibold uppercase tracking-[0.18em] text-[#84ff00]">A simple workflow</p>
            <h2 className="mt-3 max-w-2xl text-3xl font-semibold tracking-[-0.03em] sm:text-4xl">Go from a post or stream to a better next test.</h2>
            <ol className="mt-10 grid gap-6 md:grid-cols-3">
              {content.steps.map(([heading, copy], index) => <li key={heading} className="rounded-2xl border border-white/[0.09] bg-[#07060d] p-6">
                <span className="inline-grid h-8 w-8 place-items-center rounded-full text-sm font-bold" style={{ background: `${content.color}26`, color: content.color }}>{index + 1}</span>
                <h3 className="mt-5 text-lg font-semibold">{heading}</h3>
                <p className="mt-2 text-sm leading-relaxed text-white/60">{copy}</p>
              </li>)}
            </ol>
          </div>
        </section>

        <section className="mx-auto w-full max-w-4xl px-5 py-16 sm:px-8 sm:py-24">
          <p className="text-xs font-semibold uppercase tracking-[0.18em] text-[#84ff00]">Questions</p>
          <h2 className="mt-3 text-3xl font-semibold tracking-[-0.03em] sm:text-4xl">{content.label} analytics FAQ</h2>
          <div className="mt-8 divide-y divide-white/[0.09] rounded-2xl border border-white/[0.09] bg-white/[0.025] px-6">
            {content.faqs.map(([question, answer]) => <details key={question} className="group py-5">
              <summary className="cursor-pointer list-none pr-8 text-base font-semibold text-white marker:hidden">{question}</summary>
              <p className="mt-3 max-w-3xl text-sm leading-relaxed text-white/60">{answer}</p>
            </details>)}
          </div>
        </section>
      </main>
    </div>
  );
}
